/**
 * VacationBidForm
 *
 * Self-contained vacation-bid form used in two places:
 *  1. CreateVacationBidRequest  – coordinates with the outer calendar via
 *     onBidsChange / onShowHistoryChange / onEditModeChange / onSubmit
 *  2. VacationBidDetails        – inline on the right of the calendar; when
 *     hideReviewTable=true the duplicate review table is suppressed and
 *     onReviewReady fires so the parent's "Current Request" table is updated.
 */
import React, { useState, useEffect } from 'react'
import 'form-pattern/dist/main.css'
import Table from '../../../components/common/Table/Table'
import Dropdown, { DropdownOption } from '../../../components/common/Dropdown'
import ValidationBanner from '../../../components/common/ValidationBanner'
import { validateChoiceRank, validateVacationBiddingEntries, validateDateGaps } from '../../../components/common/Validation/validationEngine'
import ValidationMessages from '../../../components/common/Validation/validationMessageConfig'
import { TextInputField } from '../../../components/common/FormFields'
import { ValidationErrorDisplay } from '../../../components/common/FormFields/ValidationErrorDisplay'
import { scrollToTopAndFocusAlert } from '../../../utils/scrollUtils'
import { formatHours } from '../../../utils/formatters'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { getOtherReqDetails } from '../../../store/slices/Requestor/createNewRequestSlice'
import { isVacationBidOverlapForDate } from '../StatusViews/detail/constants'
import PencilIcon from '../../../../public/assets/images/pencil-outline.svg'
import MinusCircleIcon from '../../../../public/assets/images/minus-circle-outline.svg'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface VacationBidEntry {
  id: string
  dates: string[]
  hoursPerDay: string | Record<string, string>
  totalHours: number
  comments: string
  choiceRank?: string
}

const getDateHours = (hoursPerDay: string | Record<string, string>, dateStr: string): string => {
  if (typeof hoursPerDay === 'string') return hoursPerDay
  return hoursPerDay[dateStr] || '8'
}

/** Row shape expected by VacationBidDetails' "Current Request" table */
export interface ReviewReadyRow {
  id: string
  requestType: string
  date: string
  day: string
  timesRequested: string
  hours: string
  /** Matches legacy's "Day in Other Request(s)" checkbox — true when another existing request already has a Vacation Bid allocation on this date. */
  hoursOtherRequests: boolean
}

export interface VacationBidFormProps {
  /** Dates the form is pre-filled with (MM/DD/YYYY). */
  selectedDates: string[]
  /** Called when the user cancels. */
  onCancel: () => void

  // ---- Props for CreateVacationBidRequest coordination ----
  /** Outer calendar's edit mode flag (controlled). */
  editMode?: boolean
  /** Currently edited date (controlled). */
  editDate?: string | null
  /** Called when submit is clicked – receives final vacationBids array. */
  onSubmit?: (vacationBids: VacationBidEntry[]) => void
  /** Notifies parent whenever the bids array changes so it can update allBidDates. */
  onBidsChange?: (bids: VacationBidEntry[]) => void
  /** Notifies parent when showHistoryTable changes so calendar can switch value. */
  onShowHistoryChange?: (show: boolean) => void
  /** Notifies parent to sync editMode / editDate on the outer calendar. */
  onEditModeChange?: (editMode: boolean, editDate: string | null) => void

  // ---- Props for VacationBidDetails usage ----
  /**
   * When true, the duplicate "Dates added to Request" review table is
   * suppressed — parent is responsible for showing the rows via onReviewReady.
   */
  hideReviewTable?: boolean
  /**
   * Fires when the user confirms the form ("Next: Review Request") with the
   * updated rows in CurrentRequestSection TABLE_COLUMNS format.
   */
  onReviewReady?: (rows: ReviewReadyRow[]) => void
  /**
   * The request currently being viewed/edited (VacationBidDetails usage) —
   * excluded from its own "Day in Other Requests" overlap check, since it
   * would otherwise appear in otherReqData and match against itself.
   */
  currentRequestId?: string
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const EditButton: React.FC<{ bidId: string; dateStr: string; onEdit: (id: string, dateStr: string) => void }> = ({ bidId, dateStr, onEdit }) => (
  <button
    className="flex items-center gap-2 bg-transparent border-0 p-0 cursor-pointer hover:opacity-80"
    type="button"
    aria-label="Edit"
    onClick={() => onEdit(bidId, dateStr)}
  >
    <img src={PencilIcon} alt="Edit" className="w-4 h-4" />
  </button>
)

const RemoveButton: React.FC<{ bidId: string; dateStr: string; onRemove: (bidId: string, dateStr: string) => void }> = ({ bidId, dateStr, onRemove }) => (
  <button
    className="flex items-center gap-2 bg-transparent border-0 p-0 cursor-pointer hover:opacity-80"
    type="button"
    aria-label="Remove"
    onClick={() => onRemove(bidId, dateStr)}
  >
    <img src={MinusCircleIcon} alt="Remove" className="w-4 h-4" />
  </button>
)

/**
 * Renders the "Day in Other Requests" column as a disabled checkbox
 * (matching legacy's read-only checked/unchecked checkbox), and falls back
 * to the plain cell value for every other column.
 */
const renderOverlapCheckboxCell = (row: any, col: { key: string }): React.ReactNode => {
  if (col.key !== 'hoursOtherRequests') return row[col.key]
  if (row._totalRow) return ''
  return (
    <input
      type="checkbox"
      className="table-cell-checkbox"
      checked={!!row.hoursOtherRequests}
      disabled
      readOnly
      aria-label="Day in other requests"
    />
  )
}

const VacationBidTableDisplay: React.FC<{
  vacationBids: VacationBidEntry[]
  onEdit: (id: string, dateStr: string) => void
  onRemove: (bidId: string, dateStr: string) => void
  otherReqData: Record<string, any[]> | null | undefined
  currentRequestId?: string
}> = ({ vacationBids, onEdit, onRemove, otherReqData, currentRequestId }) => {
  const rows: any[] = []
  vacationBids.forEach((bid) => {
    bid.dates.forEach((dateStr) => {
      const dateObj = new Date(dateStr)
      const dayName = dateObj.toLocaleDateString('en-US', { weekday: 'short' })
      const formattedDate = dateObj.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
      const dateHours = getDateHours(bid.hoursPerDay, dateStr)
      rows.push({
        id: `${bid.id}-${dateStr}`,
        requestType: 'Vacation Bidding',
        date: formattedDate,
        day: dayName,
        timesRequested: '8:00 AM - 5:00 PM',
        hours: dateHours,
        hoursOtherRequests: isVacationBidOverlapForDate(otherReqData, dateStr, currentRequestId),
        edit: <EditButton bidId={bid.id} dateStr={dateStr} onEdit={onEdit} />,
        remove: <RemoveButton bidId={bid.id} dateStr={dateStr} onRemove={onRemove} />,
      })
    })
  })
  const totalHours = vacationBids.reduce((sum, b) => sum + b.totalHours, 0)
  const columns = [
    { key: 'requestType', label: 'Request Type' },
    { key: 'date', label: 'Date' },
    { key: 'day', label: 'Day' },
    { key: 'timesRequested', label: 'Times' },
    { key: 'hours', label: 'Requested Hours' },
    { key: 'hoursOtherRequests', label: 'Day in Other Requests' },
    { key: 'edit', label: 'Edit' },
    { key: 'remove', label: 'Remove' },
  ]
  return (
    <Table
      columns={columns}
      renderCell={renderOverlapCheckboxCell}
      rows={[
        ...rows,
        {
          id: 'totals-row',
          requestType: 'Total Hours',
          date: '',
          day: '',
          timesRequested: '',
          hours: formatHours(totalHours),
          hoursOtherRequests: false,
          edit: '',
          remove: '',
          _totalRow: true,
          _rowStyle: {
            backgroundColor: '#D3D6DC',
            border: '1px solid #878F9B'
          },
        },
      ]}
      showPagination={false}
    />
  )
}

// ---------------------------------------------------------------------------
// Validation helpers
// ---------------------------------------------------------------------------

const validateHoursValue = (hours: string): string | null => {
  if (!hours) return 'Hours per day is required'
  const n = Number.parseFloat(hours)
  if (Number.isNaN(n)) return 'Hours must be a number'
  if (n < 0.25 || n > 16) return 'Revise'
  return null
}

const DROPDOWN_OPTIONS: DropdownOption[] = [
  { id: 1, label: '1', value: '1' },
  { id: 2, label: '2', value: '2' },
  { id: 3, label: '3', value: '3' },
  { id: 4, label: '4', value: '4' },
  { id: 5, label: '5', value: '5' },
  { id: 6, label: 'Not applicable', value: 'Not applicable' },
]

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const VacationBidForm: React.FC<VacationBidFormProps> = ({
  selectedDates: initialDates,
  onCancel,
  editMode: controlledEditMode,
  editDate: controlledEditDate,
  onSubmit,
  onBidsChange,
  onShowHistoryChange,
  onEditModeChange,
  hideReviewTable = false,
  onReviewReady,
  currentRequestId,
}) => {
  // When controlled by parent (CreateVacationBidRequest), use those values;
  // otherwise manage locally (VacationBidDetails).
  const isControlled = onEditModeChange !== undefined
  const [localEditMode, setLocalEditMode] = useState<boolean>(false)
  const [localEditDate, setLocalEditDate] = useState<string | null>(null)
  const editMode = isControlled ? (controlledEditMode ?? false) : localEditMode
  const editDate = isControlled ? (controlledEditDate ?? null) : localEditDate

  const setEditState = (em: boolean, ed: string | null) => {
    if (isControlled) onEditModeChange?.(em, ed)
    else { setLocalEditMode(em); setLocalEditDate(ed) }
  }

  const [selectedDates, setSelectedDates] = useState<string[]>(initialDates)
  const [hoursPerDay, setHoursPerDay] = useState<string>('8')
  const [vacationBids, setVacationBids] = useState<VacationBidEntry[]>([])

  const dispatch = useAppDispatch()
  const otherReqData = useAppSelector((state) => state.otherReqDetails.data)

  // Fetch other-request details for every month the currently-selected dates
  // and already-added bids span, so the "Day in Other Requests" checkbox
  // reflects real overlaps rather than always showing unchecked.
  useEffect(() => {
    const allDates = [
      ...selectedDates,
      ...vacationBids.flatMap((bid) => bid.dates),
    ]
    const monthsYears = new Set(
      allDates.map((date) => {
        const parsed = new Date(date)
        return `${parsed.getMonth()}-${parsed.getFullYear()}`
      })
    )
    monthsYears.forEach((key) => {
      const [month, year] = key.split('-').map(Number)
      dispatch(getOtherReqDetails({ month, year }))
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDates.join(','), vacationBids.flatMap((bid) => bid.dates).join(',')])
  const [showHistoryTableState, setShowHistoryTableState] = useState<boolean>(false)
  const showHistoryTable = showHistoryTableState
  const [editingBidId, setEditingBidId] = useState<string | null>(null)
  const [originalEditingBid, setOriginalEditingBid] = useState<VacationBidEntry | null>(null)
  const [comments, setComments] = useState<string>('')
  const [selectedRank, setSelectedRank] = useState<DropdownOption | null>(null)
  const [bannerMessages, setBannerMessages] = useState<string[] | null>(null)
  const [hoursError, setHoursError] = useState<string>('')
  const [choiceRankError, setChoiceRankError] = useState<string>('')

  const setShowHistoryTable = (show: boolean) => {
    setShowHistoryTableState(show)
    onShowHistoryChange?.(show)
  }

  // Sync when parent changes which date to edit (calendar click in CreateVacationBidRequest)
  useEffect(() => {
    if (isControlled && controlledEditMode && controlledEditDate) {
      // Parent signalled to enter edit mode for a specific date
      const containingBid = vacationBids.find((b) => b.dates.includes(controlledEditDate))
      if (containingBid) {
        setOriginalEditingBid(containingBid)
        setSelectedDates(containingBid.dates)
        setHoursPerDay(getDateHours(containingBid.hoursPerDay, controlledEditDate))
        setSelectedRank(DROPDOWN_OPTIONS.find((o) => o.value === containingBid.choiceRank) || null)
        setVacationBids((prev) => prev.filter((b) => b.id !== containingBid.id))
        setEditingBidId(containingBid.id)
        setShowHistoryTableState(false)
      }
    }
  }, [controlledEditMode, controlledEditDate]) // eslint-disable-line react-hooks/exhaustive-deps

  // Sync initialDates when parent changes the clicked date (VacationBidDetails)
  useEffect(() => {
    if (!isControlled) {
      setSelectedDates(initialDates)
      setShowHistoryTableState(false)
      setLocalEditMode(false)
      setLocalEditDate(null)
      setHoursPerDay('8')
      setSelectedRank(null)
      setBannerMessages(null)
      setHoursError('')
      setChoiceRankError('')
    } else if (initialDates.length > 0 && !controlledEditMode) {
      // Controlled mode: new dates selected on calendar while history table is visible —
      // switch back to the entry form so the user can fill in details for the new selection.
      setSelectedDates(initialDates)
      setShowHistoryTableState(false)
      setBannerMessages(null)
      setHoursError('')
      setChoiceRankError('')
    }
  }, [initialDates.join(',')]) // eslint-disable-line react-hooks/exhaustive-deps

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr)
    const mm = String(date.getMonth() + 1).padStart(2, '0')
    const dd = String(date.getDate()).padStart(2, '0')
    return `${mm}/${dd}/${date.getFullYear()}`
  }

  const sortedDates = [...selectedDates]
    .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())
    .map(formatDate)

  const totalHours = selectedDates.length * (Number.parseFloat(hoursPerDay) || 0)

  const buildReviewRows = (bids: VacationBidEntry[]): ReviewReadyRow[] => {
    const rows: ReviewReadyRow[] = []
    bids.forEach((bid, bi) => {
      bid.dates.forEach((dateStr, di) => {
        const dateObj = new Date(dateStr)
        const formattedDate = dateObj.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
        const dayName = dateObj.toLocaleDateString('en-US', { weekday: 'short' })
        const hrs = getDateHours(bid.hoursPerDay, dateStr)
        rows.push({
          id: `row-${bi}-${di}`,
          requestType: 'Vacation Bidding',
          date: formattedDate,
          day: dayName,
          timesRequested: '8:00 AM - 5:00 PM',
          hours: String(Number.parseFloat(hrs).toFixed(2)),
          hoursOtherRequests: isVacationBidOverlapForDate(otherReqData, dateStr, currentRequestId),
        })
      })
    })
    return rows
  }

  const validateAndGetErrors = () => {
    const errorMessages: string[] = []
    let hasHoursError = false
    let hasChoiceRankError = false
    const hoursValidationError = validateHoursValue(hoursPerDay)
    if (hoursValidationError) {
      hasHoursError = true
      setHoursError(hoursValidationError)
      errorMessages.push(hoursValidationError === 'Revise' ? ValidationMessages.DURATION : hoursValidationError)
    } else {
      const { messages: entryMessages } = validateVacationBiddingEntries({ vacation: hoursPerDay })
      if (entryMessages?.includes(ValidationMessages.DURATION)) {
        hasHoursError = true
        setHoursError(ValidationMessages.DURATION)
        errorMessages.push(ValidationMessages.DURATION)
      }
    }
    if (!selectedRank) {
      hasChoiceRankError = true
      setChoiceRankError('Required')
      errorMessages.push('Choice/Rank is required')
    }
    return { errorMessages, hasHoursError, hasChoiceRankError }
  }

  const handleAddToVacationBid = () => {
    if (selectedDates.length === 0) return
    const { errorMessages, hasHoursError, hasChoiceRankError } = validateAndGetErrors()
    const dateGapError = validateDateGaps(selectedDates)
    if (dateGapError) errorMessages.push(dateGapError)
    if (hasHoursError || hasChoiceRankError || dateGapError) { setBannerMessages(errorMessages); scrollToTopAndFocusAlert(); return }
    const choiceErr = validateChoiceRank(selectedRank?.value)
    if (choiceErr) { setBannerMessages([choiceErr]); scrollToTopAndFocusAlert(); return }

    const newBid: VacationBidEntry = {
      id: Date.now().toString(),
      dates: selectedDates,
      hoursPerDay,
      totalHours,
      comments: '',
      choiceRank: selectedRank?.value,
    }
    setBannerMessages(null); setHoursError(''); setChoiceRankError('')
    const updatedBids = [...vacationBids, newBid]
    setVacationBids(updatedBids)
    onBidsChange?.(updatedBids)
    setSelectedDates([])
    setHoursPerDay('8')
    setSelectedRank(null)
    setShowHistoryTable(true)
    onReviewReady?.(buildReviewRows(updatedBids))
  }

  const removeDateFromBid = (bid: VacationBidEntry, dateStr: string): VacationBidEntry | null => {
    const updatedDates = bid.dates.filter((d) => d !== dateStr)
    if (updatedDates.length === 0) return null
    let updatedHours: string | Record<string, string> = bid.hoursPerDay
    if (typeof bid.hoursPerDay !== 'string') {
      const map = { ...bid.hoursPerDay }
      delete map[dateStr]
      updatedHours = map
    }
    const updatedTotal = updatedDates.reduce((sum, d) => {
      const hoursMap = updatedHours as Record<string, string>
      const h = typeof updatedHours === 'string' ? updatedHours : hoursMap[d] || '0'
      return sum + (Number.parseFloat(h) || 0)
    }, 0)
    return { ...bid, dates: updatedDates, hoursPerDay: updatedHours, totalHours: updatedTotal }
  }

  const handleRemoveBid = (bidId: string, dateStr: string) => {
    setVacationBids((prev) => {
      const updated = prev
        .map((bid) => bid.id === bidId ? removeDateFromBid(bid, dateStr) : bid)
        .filter((b): b is VacationBidEntry => b !== null)
      onBidsChange?.(updated)
      onReviewReady?.(buildReviewRows(updated))
      return updated
    })
  }

  const handleEditBid = (id: string, dateStr: string) => {
    const bidToEdit = vacationBids.find((b) => b.id === id)
    if (!bidToEdit) return
    setOriginalEditingBid(bidToEdit)
    setSelectedDates(bidToEdit.dates)
    setHoursPerDay(getDateHours(bidToEdit.hoursPerDay, dateStr))
    setSelectedRank(DROPDOWN_OPTIONS.find((o) => o.value === bidToEdit.choiceRank) || null)
    setVacationBids((prev) => prev.filter((b) => b.id !== id))
    setEditingBidId(id)
    setEditState(true, dateStr)
    setShowHistoryTable(false)
  }

  const handleBackFromEdit = () => {
    setEditingBidId(null)
    setOriginalEditingBid(null)
    setEditState(false, null)
    setShowHistoryTable(true)
  }

  const resetFormState = () => {
    setEditingBidId(null)
    setOriginalEditingBid(null)
    setSelectedDates([])
    setHoursPerDay('8')
    setSelectedRank(null)
    setShowHistoryTable(true)
    setBannerMessages(null)
    setHoursError('')
    setChoiceRankError('')
    setEditState(false, null)
  }

  const handleUpdateBid = () => {
    const { errorMessages, hasHoursError, hasChoiceRankError } = validateAndGetErrors()
    // The per-date hours-only edit path below (editingBidId && editDate && originalEditingBid)
    // doesn't change the bid's date set, so only re-check the gap when a new date set is being committed.
    const isDateSetChanging = !(editingBidId && editDate && originalEditingBid)
    const dateGapError = isDateSetChanging ? validateDateGaps(selectedDates) : null
    if (dateGapError) errorMessages.push(dateGapError)
    if (hasHoursError || hasChoiceRankError || dateGapError) { setBannerMessages(errorMessages); scrollToTopAndFocusAlert(); return }

    let updatedBids: VacationBidEntry[]
    if (editingBidId && editDate && originalEditingBid) {
      const hoursMap = (typeof originalEditingBid.hoursPerDay === 'string'
        ? Object.fromEntries(originalEditingBid.dates.map((d) => [d, originalEditingBid.hoursPerDay as string]))
        : { ...originalEditingBid.hoursPerDay }) as Record<string, string>
      hoursMap[editDate] = hoursPerDay
      const newTotal = Object.values(hoursMap).reduce((sum, h) => sum + (Number.parseFloat(h) || 0), 0)
      const updatedBid: VacationBidEntry = { ...originalEditingBid, hoursPerDay: hoursMap, totalHours: newTotal, comments, choiceRank: selectedRank?.value }
      updatedBids = [...vacationBids, updatedBid]
    } else {
      updatedBids = [...vacationBids, { id: editingBidId || Date.now().toString(), dates: selectedDates, hoursPerDay, totalHours: selectedDates.length * (Number.parseFloat(hoursPerDay) || 0), comments, choiceRank: selectedRank?.value }]
    }
    setVacationBids(updatedBids)
    onBidsChange?.(updatedBids)
    setOriginalEditingBid(null)
    setHoursError('')
    onReviewReady?.(buildReviewRows(updatedBids))
    resetFormState()
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const handleRankSelect = (opt: DropdownOption) => {
    setSelectedRank(opt)
    if (choiceRankError) setChoiceRankError('')
    if (bannerMessages) setBannerMessages(null)
  }

  const renderBannerMessages = () => {
    if (!bannerMessages || bannerMessages.length === 0) return null
    return (
      <div className="space-y-3 mb-8! mt-4">
        {bannerMessages.map((msg) => (
          <ValidationBanner key={msg} variant="error" heading={<p className="p1 -medium">{msg}</p>} compact />
        ))}
      </div>
    )
  }

  const renderFormSection = () => {
    if (selectedDates.length === 0 && !editMode) {
      return <p className="p3 -medium pt-2 tablet:pt-4 mobile:pt-2">Select a date on the calendar to add time to your request</p>
    }
    return (
      <>
        {editMode ? (
          <>
            <h2 className="h2 text-lg md:text-xl m-0">Edit Vacation Bid</h2>
            <p className="p3 -book pt-2 text-sm md:text-base">Editing date: {editDate}</p>
            {/* <div className="mt-2"><button className="button -light" type="button" onClick={handleRemoveEditDate}>Remove Date From Request</button></div> */}
          </>
        ) : (
          <>
            <h2 className="h2 text-lg md:text-xl m-0">Request Vacation Bid</h2>
            <p className="p3 -book pt-2 text-sm md:text-base">Adding Time Off Hours For:</p>
            {sortedDates.length > 0 && (
              <ul className="list-disc">{sortedDates.map((d) => <li key={d} className="p3 -book text-sm md:text-base">{d}</li>)}</ul>
            )}
          </>
        )}

        <p className="p3 -medium pt-4 text-sm md:text-base">Duration</p>
        <div className="flex flex-col gap-3 pt-2">
          <label htmlFor="radio-duration-full" className="inline-flex items-center gap-3 cursor-pointer">
            <input id="radio-duration-full" name="radio-duration" type="radio" value="Full Day" defaultChecked className="radio-button" />
            <span className="p3 -medium text-sm md:text-base">Full Day</span>
          </label>
        </div>

        {/* Hours input */}
        <div className="pt-4 mt-6!">
          <div className="hidden md:flex gap-2 md:gap-6 items-start pb-3 border-b border-gray-300 w-full">
            <div className="w-full md:w-auto md:min-w-[250px]"><p className="p3 -medium text-sm md:text-base">Hours Per Day</p></div>
            <div className="flex gap-2 md:gap-4 w-full md:w-auto md:flex-1"><div className="flex-1"><p className="p3 -medium text-sm md:text-base">Time Off Type</p></div></div>
          </div>
          <div className="flex flex-col md:flex-row gap-2 md:gap-6 items-start pt-2 md:pt-4 mb-8 pb-6">
            <div className="w-full md:w-auto md:min-w-[200px]">
              <TextInputField
                id="hours-input" name="hoursPerDay" label="Hours Per Day" type="text" placeholder="0"
                value={hoursPerDay}
                onChange={(e) => { setHoursPerDay(e.target.value); if (hoursError) setHoursError('') }}
                error={hoursError} customStyles={{ width: '100%' }}
              />
            </div>
            <div className="flex items-center lg:mt-4! justify-center w-full md:w-auto h-full">
              <p className="p3 -book text-sm md:text-base text-center pl-9!">Vacation Bidding<span className="text-red-500">*</span></p>
            </div>
          </div>
        </div>

        <p className="p2 -book text-sm md:text-base mt-4!">
          <span className="text-red-500">*</span>Vacation Bidding Requests do not update your Vacation (PTO/ETO) Available Balance until they have been approved.
        </p>

        {/* Choice Rank */}
        <div className="mt-3 w-full md:w-auto lg:max-w-xs">
          <label htmlFor="choice-rank-dropdown" className="ds-dropdown__label">
            <span className="ds-dropdown__label-text">Choice Rank<sup className="align-super text-red-500">*</sup></span>
          </label>
          <div>
            <Dropdown
              selectedValue={selectedRank || undefined}
              options={DROPDOWN_OPTIONS}
              onSelect={handleRankSelect}
              placeholder="Select a choice rank"
              menuClassName="max-w-full max-h-[20rem]!"
            />
            {choiceRankError && <ValidationErrorDisplay message={choiceRankError} id="choice-rank-error" />}
          </div>
        </div>

        <p className="p2 -book text-sm md:text-base">{formatHours(Number.parseFloat(hoursPerDay || '0'))} Hours requested per day</p>
        <p className="p2 -book text-sm md:text-base">{formatHours(totalHours)} Hours Total</p>

        <div className="flex flex-col-reverse md:flex-row items-stretch md:items-center gap-3 mt-8! pb-6">
          {editMode ? (
            <>
              <button type="button" className="button -light block w-full md:w-auto" onClick={handleBackFromEdit}>Back</button>
              <button type="button" className="button -light block w-full md:w-auto" onClick={onCancel}>Cancel Bid</button>
              <button type="button" className="button -primary -light block w-full md:w-auto" onClick={handleUpdateBid}>Update</button>
            </>
          ) : (
            <>
              <button type="button" className="button -light block w-full md:w-auto" onClick={onCancel}>Cancel Bid</button>
              <button type="button" className="button -primary -light block w-full md:w-auto" onClick={handleAddToVacationBid}>Next: Review Request</button>
            </>
          )}
        </div>
      </>
    )
  }

  const renderHistorySection = () => (
    <>
      {!hideReviewTable && (
        <>
          <h2 className="h2 m-0 mb-4 text-lg md:text-xl">Dates added to Request</h2>
          <p className="p3 -book">To add a date, select it on the calendar. To edit or remove a date you may use the table.</p>
          <div className="overflow-x-auto mt-8">
            <VacationBidTableDisplay vacationBids={vacationBids} onEdit={handleEditBid} onRemove={handleRemoveBid} otherReqData={otherReqData} currentRequestId={currentRequestId} />
          </div>
        </>
      )}
      <div className="form-group margin-top-2u mt-8">
        <label id="comments-label" className="p4 -medium" htmlFor="comments-textarea">Comments</label>
        <div className="textarea-wrapper w-full relative">
          <textarea
            autoComplete="on" id="comments-textarea" name="comments" aria-labelledby="comments-label comments-count"
            className="text-area_word-count text-area_global -book text-base! text-[#0D1C3D]! w-full!"
            data-show-lines="5" rows={5} maxLength={1000}
            onChange={(e) => setComments(e.target.value)} value={comments}
          />
          <div className="character-count">
            <span id="comments-count" aria-live="polite" aria-atomic="true" className="count-area text-input-helper"
              data-description=" of 1000 characters left" data-full-text="No more characters available, 1000 of 1000 characters left">
              {1000 - comments.length} of 1000 characters left
            </span>
          </div>
        </div>
      </div>
      <div className="flex flex-col-reverse lg:mt-8! md:flex-row items-stretch md:items-center gap-3 mt-8 pb-6">
        <button type="button" className="button -light block w-full md:w-auto" onClick={onCancel}>Cancel Bid</button>
        <button type="button" className="button -light block w-full md:w-auto" onClick={onCancel}>Save as Draft</button>
        <button type="button" className="button -primary -light block w-full md:w-auto" onClick={() => onSubmit?.(vacationBids)}>Submit Vacation Bid</button>
      </div>
    </>
  )

  return (
    <div>
      {renderBannerMessages()}
      {!showHistoryTable && renderFormSection()}
      {showHistoryTable && renderHistorySection()}
    </div>
  )
}

export default VacationBidForm
